import React, { useContext, useEffect, useRef } from "react";
import Card from "./Card";
import styled from "@emotion/styled";
import Context from "../ProductsProvider";
import Message from "../Message";
import ActiveFilters from "../Filters/ActiveFilters";
import Pagination from "../../Pagination/Pagination";
import DropDownContainer from "../Filters/DropdownContainer";
import Search from "../Filters/Search";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";
import gsap from 'gsap';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from '@gsap/react';

//style rules
import { Sizes, Typography, Colors } from "../../../styles";

const CardsColumn = () => {
	const { data: { language, items, message, postCount, dialog, dialogIsOpen },
		actions: { setDialogIsOpen },
	} = useContext(Context);

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.large})`,
	});

	const noItemsText = () => {
		if (language === "en") return "No products found";
		return "Aucun produit trouvé";
	}

	const renderGridContent = (items) => {
		if (message) return <Message text={message} />;

		if (items.length === 0) return <Message text={noItemsText()} />;

		return (
			<CardsList>
				<Card />
			</CardsList>
		)
	}

	const renderResultsCount = (postCount) => {
		if (language === "en") {
			if (postCount === 1) return "1 result";
			return `${postCount} results`;
		} else {
			if (postCount === 1) return "1 résultat";
			return `${postCount} résultats`;
		}
	}

	const OpenFiltersButtonText = () => {
		if (language === "en") return "Filter products";
		return "Filtrer les produits";
	};

	const handleOpenFiltersButtonClick = (dialog) => {
		dialog.showModal();
		setDialogIsOpen(true);
	};

	useEffect(() => {
		if (dialogIsOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
	}, [dialogIsOpen]);

	const fixedButtons = useRef();
	gsap.registerPlugin(useGSAP);
	gsap.registerPlugin(ScrollTrigger);

	useGSAP(() => {
		let mm = gsap.matchMedia();
		const isMobile = `(max-width: ${Sizes.screens.large})`;

		// opacity animation for the element
		const showAnim = gsap.to(fixedButtons.current, {
			opacity: 0,
			paused: true,
		});

		mm.add({ isMobile }, () => {
			// initially setting visibility back to visible (to avoid flash of content on load)
			gsap.set(fixedButtons.current, { visibility: "visible", zIndex: 100,
			});

			ScrollTrigger.create({
				trigger: fixedButtons.current,
				pin: fixedButtons.current,
				pinSpacing: false,
				start: "top 20px",
				end: "top+=13100px",
				onUpdate: (self) => {
					if (self.progress === 0) return;

					if (self.progress >= 0.1) {
						self.direction === 1 ? showAnim.play() : showAnim.reverse();
					}
				},
			});
		});

	}, []);

	return (
		<RightColumn>
			<CardsGridHeader>
				<ResultsSearchContainer>
					<Results>{renderResultsCount(postCount)}</Results>
					{
						isDesktopOrLaptop &&
						<Search />
					}
				</ResultsSearchContainer>
				<div>
					<ActiveFilters />
				</div>
				<ButtonsContainer ref={fixedButtons} language={language}>
					{
						!isDesktopOrLaptop &&
						<OpenFiltersButton
							type="button"
							onClick={() => handleOpenFiltersButtonClick(dialog.current)}
						>
							{OpenFiltersButtonText()}
							<img src={`${mainScript.assetPath}images/svg/icons/icon-filters.svg`} alt="" />
						</OpenFiltersButton>
					}
					<DropDownContainer />
				</ButtonsContainer>
			</CardsGridHeader>
			{renderGridContent(items)}
			<Pagination />
		</RightColumn>
	)
};

export default CardsColumn;

CardsColumn.propTypes = {
	dialogRef: PropTypes.object,
}

const RightColumn = styled.div`
`;

const CardsGridHeader = styled.div`
	@media only screen and (min-width: ${Sizes.screens.large}) {
		display: grid;
		grid-template-columns: auto 1fr 17rem;
		align-items: center;
		column-gap: 2rem;
	}

	.pin-spacer {
		margin-top: 2rem !important;
	}
`;

const ResultsSearchContainer = styled.div`
	@media only screen and (min-width: ${Sizes.screens.large}) {
		display: grid;
		grid-template-columns: 10rem 2rem;
		align-items: center;
		justify-content: start;
		column-gap: 2rem;
	}
`;

const CardsList = styled.ul`
	list-style: none;
	padding: unset;
	margin: 4rem 0 4rem 0;
	display: grid;
	row-gap: 3rem;
	grid-template-columns: 1fr;
	grid-auto-rows: auto;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 3rem 4rem;
		margin-top: 3rem;
    }

	@media only screen and (min-width: calc(${Sizes.screens.large} + 376px)) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 3rem 4rem;
    }

`;

const Results = styled.p`
	font-size: 1.5rem;
	font-weight: ${Typography.fontWeight.bold};
	line-height: 1.9rem;
	letter-spacing: -0.003rem;
`;

const OpenFiltersButton = styled.button`
	all: unset;
	font-size: 1.5rem;
	font-weight: ${Typography.fontWeight.bold};
	line-height: 1.6rem;
	letter-spacing: -0.015rem;
	display: flex;
	flex-wrap: nowrap;
	column-gap: 0.7rem;
	background-color: ${Colors.fp.pineGreen40};
	padding: 2rem 1rem;
	align-items: center;
	justify-content: center;
	border-right: 1px solid ${Colors.base.black};
	cursor: pointer;
`;

const ButtonsContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	position: relative;
	width: 100vw;
	left: 50%;
	right: 50%;
	margin-left: -50vw;
	margin-right: -50vw;
	max-width: unset;
	margin-top: 4rem;

	${OpenFiltersButton}{
		font-size: ${props => props.language !== "en" ? "1.4rem" : "1.5rem"};
		line-height: ${props => props.language !== "en" ? "1.7rem" : "1.8rem"}
	}

	@media only screen and (min-width: ${Sizes.screens.large}) {
		position: static;
		display: block;
		width: unset;
		margin: unset;
	}
`;
